import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TOKEN_TYPE, getTokenFromCookies, clearAllTokens, setTokenToCookies, setTokenExpiry, isTokenExpiring } from '../../../lib/cookies';
import { authAPI } from '../api/api';
import {
  setLoading,
  setError,
  loginSuccess,
  loginOTPRequired,
  logout as logoutAction,
  clearOTPState as clearOTPStateAction,
} from '../store/authSlice';
import {
  selectAuth,
  selectUser,
  selectIsAuthenticated,
} from '../store/selectors';

export const useAuth = () => {
  const dispatch = useDispatch();
  const authState = useSelector(selectAuth);
  const user = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Refresh token function
  const refreshToken = async (): Promise<boolean> => {
    const refreshTokenValue = getTokenFromCookies(TOKEN_TYPE.REFRESH_TOKEN);
    if (!refreshTokenValue) {
      clearAllTokens();
      dispatch(logoutAction());
      return false;
    }

    try {
      const response = await authAPI.refreshToken(refreshTokenValue);

      if (response.success) {

        // Save new tokens
        const expiresAt = new Date(response.data.expiredAt);
        const expiresInSeconds = Math.floor((expiresAt.getTime() - Date.now()) / 1000);

        setTokenToCookies(TOKEN_TYPE.ACCESS_TOKEN, response.data.accessToken, expiresInSeconds);
        setTokenToCookies(TOKEN_TYPE.REFRESH_TOKEN, response.data.refreshToken);
        setTokenExpiry(response.data.expiredAt);

        // Schedule next refresh
        scheduleTokenRefresh();

        return true;
      } else {
        throw new Error('Refresh failed');
      }
    } catch (error: any) {
      clearAllTokens();
      dispatch(logoutAction());
      return false;
    }
  };

  // Schedule automatic token refresh
  const scheduleTokenRefresh = () => {
    // Clear existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    const expiresAt = getTokenFromCookies(TOKEN_TYPE.EXPIRES_AT);
    if (!expiresAt) return;

    const expireTime = new Date(expiresAt);
    const now = new Date();
    const refreshTime = new Date(expireTime.getTime() - 30 * 1000); // 30 seconds before expiry
    const timeUntilRefresh = refreshTime.getTime() - now.getTime();

    if (timeUntilRefresh > 0) {
      refreshTimeoutRef.current = setTimeout(() => {
        refreshToken();
      }, timeUntilRefresh);
    }
  };

  // Check authentication status on mount
  const checkAuth = async () => {
    const token = getTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN);

    if (!token) {
      dispatch(setLoading(false));
      return;
    }

    // Check if token is expiring and refresh if needed
    if (isTokenExpiring()) {
      const refreshSuccess = await refreshToken();
      if (!refreshSuccess) {
        return; // Failed to refresh, already logged out
      }
    } else {
      // Token is still valid, schedule refresh
      scheduleTokenRefresh();
    }

    // If user data already exists in Redux, no need to call API
    if (user && isAuthenticated) {
      dispatch(setLoading(false));
      return;
    }

    try {
      // Call /me API to verify token and get user data
      const response = await authAPI.me();

      if (response.success) {
        dispatch(loginSuccess(response.data));
      } else {
        throw new Error('Invalid token');
      }
    } catch (error: any) {
      // Token is invalid or expired
      clearAllTokens();
      dispatch(logoutAction());
    }
  };

  useEffect(() => {
    let isMounted = true;

    const runCheckAuth = async () => {
      if (isMounted) {
        await checkAuth();
      }
    };

    runCheckAuth();

    return () => {
      isMounted = false;
      // Clear refresh timeout on unmount
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, []);

  // Password login
  const login = async (email: string, password: string) => {
    const data = await authAPI.login(email, password);

    if (!data.success) {
      dispatch(setError(data.message));
      throw new Error(data.message);
    }

    // Check message code to determine flow
    if (data.messageCode === 'PASSWORD_LOGIN_SUCCESS') {
      // Save tokens with expiry
      const expiresAt = new Date(data.data.expiredAt);
      const expiresInSeconds = Math.floor((expiresAt.getTime() - Date.now()) / 1000);


      setTokenToCookies(TOKEN_TYPE.ACCESS_TOKEN, data.data.accessToken, expiresInSeconds);
      setTokenToCookies(TOKEN_TYPE.REFRESH_TOKEN, data.data.refreshToken);
      setTokenExpiry(data.data.expiredAt);

      // Verify cookies were set

      // Schedule automatic token refresh
      scheduleTokenRefresh();

      dispatch(loginSuccess(data.data.user));
      return { success: true, user: data.data.user };
    } else if (data.messageCode === 'PASSWORD_OTP_REQUIRED') {
      dispatch(loginOTPRequired(data.data.email));
      return { success: false, needsOTP: true, email: data.data.email, message: data.message };
    }

    throw new Error('Unexpected login response');
  };

  // Register
  const register = async (email: string, password: string, displayName: string) => {
    const data = await authAPI.register(email, password, displayName);

    if (!data.success) {
      dispatch(setError(data.message));
      throw new Error(data.message);
    }

    return { success: true, email: data.data?.email, message: data.message };
  };

  // OTP Login (for activation or 2FA)
  const otpLogin = async (email: string, otpCode: string) => {
    const data = await authAPI.otpLogin(email, otpCode);

    if (!data.success) {
      dispatch(setError(data.message));
      throw new Error(data.message);
    }

    // Check if this is activation or login
    if (data.data.requireActivation === false) {
      dispatch(clearOTPStateAction());
      return { success: true, activated: true, message: data.message };
    } else if (data.data.accessToken) {
      // Save tokens with expiry
      const expiresAt = new Date(data.data.expiredAt);
      const expiresInSeconds = Math.floor((expiresAt.getTime() - Date.now()) / 1000);

      setTokenToCookies(TOKEN_TYPE.ACCESS_TOKEN, data.data.accessToken, expiresInSeconds);
      setTokenToCookies(TOKEN_TYPE.REFRESH_TOKEN, data.data.refreshToken);
      setTokenExpiry(data.data.expiredAt);

      // Schedule automatic token refresh
      scheduleTokenRefresh();

      dispatch(loginSuccess(data.data.user));
      return { success: true, user: data.data.user };
    }

    throw new Error('Unexpected OTP response');
  };

  // Request new OTP
  const requestOTP = async (email: string) => {
    const data = await authAPI.requestOTP(email);

    if (!data.success) {
      throw new Error(data.message);
    }

    return { success: true, message: data.message };
  };

  // Logout
  const logout = async () => {
    // Clear refresh timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    try {
      await authAPI.logout();
    } catch (error) {
      console.warn('Logout request failed:', error);
    } finally {
      clearAllTokens();
      dispatch(logoutAction());
    }
  };

  // Role checking helpers
  const hasRole = (requiredRoles: string | string[]) => {
    if (!user) return false;
    const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
    return roles.includes(user.role);
  };

  const hasAdminAccess = () => {
    if (!user) return false;
    return user.role === 'admin';
  };

  const canPerformWrite = () => {
    if (!user) return false;
    return user.role === 'admin';
  };

  // Clear OTP state
  const clearOTPState = () => {
    dispatch(clearOTPStateAction());
  };

  return {
    ...authState,
    login,
    register,
    otpLogin,
    requestOTP,
    logout,
    checkAuth,
    hasRole,
    hasAdminAccess,
    canPerformWrite,
    clearOTPState,
  };
};